import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  Webhook, Search, ArrowDownLeft, ArrowUpRight, CheckCircle2,
  XCircle, AlertTriangle, Clock, ChevronLeft, ChevronRight, Eye, X,
  Activity, Filter,
} from "lucide-react";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useParams } from "react-router-dom";
import * as webhookLogService from "@/shared/api/services/webhookLogService";
import { auth } from "@/shared/api/graphqlClient";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const translateStatus = (status, t) => {
  const map = { received: "received", validated: "validated", processed: "processed", failed: "failed", invalid_signature: "invalidSignature", duplicate: "duplicate" };
  return t(map[status] || status) || status;
};

const STATUS_STYLE = {
  received: { icon: Clock, cls: "mwh-s-received" },
  validated: { icon: CheckCircle2, cls: "mwh-s-validated" },
  processed: { icon: CheckCircle2, cls: "mwh-s-processed" },
  failed: { icon: XCircle, cls: "mwh-s-failed" },
  invalid_signature: { icon: AlertTriangle, cls: "mwh-s-invalid" },
  duplicate: { icon: AlertTriangle, cls: "mwh-s-duplicate" },
};

const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

.mwh-root {
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #e2f4ff;
  padding: 0 0 48px;
}
.mwh-root * { box-sizing: border-box; }

.mwh-h1 { color: #e2f4ff; font-size: 1.6rem; font-weight: 800; letter-spacing: -0.03em; }
.mwh-sub { color: #2a4a65; font-size: 13px; font-weight: 500; margin-top: 4px; }

/* ── Filter bar ── */
.mwh-filter {
  background: linear-gradient(145deg, rgba(10,22,46,0.95), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08);
  border-radius: 18px; padding: 16px 20px;
  display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
  position: relative; overflow: hidden;
}
.mwh-filter::before {
  content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}

.mwh-search { position: relative; flex: 1; min-width: 200px; }
.mwh-search input {
  width: 100%;
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 12px; padding: 10px 14px 10px 40px;
  color: #e2f4ff; font-size: 13.5px; font-weight: 500;
  outline: none; font-family: 'Plus Jakarta Sans', sans-serif;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}
.mwh-search input::placeholder { color: #1e3a55; }
.mwh-search input:focus {
  border-color: rgba(34,211,238,0.38);
  background: rgba(34,211,238,0.06);
  box-shadow: 0 0 0 4px rgba(34,211,238,0.07);
}
.mwh-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); pointer-events: none; transition: color 0.2s; }
.mwh-search:focus-within .mwh-search-icon { color: #22d3ee !important; }

.mwh-sel {
  background: rgba(34,211,238,0.04) !important; border: 1px solid rgba(34,211,238,0.1) !important;
  color: #e2f4ff !important; border-radius: 12px !important; min-width: 150px;
  font-family: 'Plus Jakarta Sans', sans-serif !important; font-weight: 500 !important;
  transition: border-color 0.22s !important;
}
.mwh-sel:hover, .mwh-sel[data-state=open] {
  border-color: rgba(34,211,238,0.35) !important;
  box-shadow: 0 0 16px rgba(34,211,238,0.08) !important;
}

/* ── Table card ── */
.mwh-table-card {
  background: linear-gradient(145deg, rgba(10,22,46,0.95), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08);
  border-radius: 22px; overflow: hidden;
  position: relative;
}
.mwh-table-card::before {
  content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}
.mwh-table { width: 100%; border-collapse: collapse; }
.mwh-thead th {
  text-align: left; padding: 14px 18px;
  font-size: 10px; font-weight: 800; color: #2a4a65;
  text-transform: uppercase; letter-spacing: 0.1em;
  background: rgba(0,0,0,0.3);
  border-bottom: 1px solid rgba(34,211,238,0.07); white-space: nowrap;
}

.mwh-row { border-bottom: 1px solid rgba(34,211,238,0.04); cursor: pointer; position: relative; }
.mwh-row:last-child { border-bottom: none; }
.mwh-row::after {
  content: ''; position: absolute; inset: 0;
  background: rgba(34,211,238,0.04); opacity: 0; transition: opacity 0.18s;
  pointer-events: none;
}
.mwh-row:hover::after { opacity: 1; }
.mwh-row:hover .mwh-eye { opacity: 1; transform: scale(1); }

.mwh-td { padding: 14px 18px; vertical-align: middle; }

/* Direction icon */
.mwh-dir { width: 36px; height: 36px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1); }
.mwh-row:hover .mwh-dir { transform: scale(1.1); }
.mwh-dir.in { background: rgba(14,165,233,0.12); }
.mwh-dir.out { background: rgba(139,92,246,0.12); }
.mwh-dir.in svg { color: #38bdf8; }
.mwh-dir.out svg { color: #a78bfa; }

/* IDs */
.mwh-wid { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #c8dff0; font-weight: 600; }
.mwh-tid { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #2a4a65; margin-top: 3px; }

/* Event badge */
.mwh-event {
  display: inline-block; background: rgba(34,211,238,0.06);
  border: 1px solid rgba(34,211,238,0.14); border-radius: 8px;
  padding: 3px 10px; font-size: 11px; font-weight: 700; color: #5a8aaa;
  transition: all 0.18s cubic-bezier(0.34,1.56,0.64,1);
}
.mwh-row:hover .mwh-event { border-color: rgba(34,211,238,0.3); color: #22d3ee; }

/* Status badges */
.mwh-status { display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 5px 12px; font-size: 11px; font-weight: 800; white-space: nowrap; }
.mwh-s-received   { background: rgba(14,165,233,0.1);  color: #38bdf8; border: 1px solid rgba(14,165,233,0.22); }
.mwh-s-validated  { background: rgba(16,185,129,0.1);  color: #34d399; border: 1px solid rgba(16,185,129,0.22); }
.mwh-s-processed  { background: rgba(16,185,129,0.1);  color: #34d399; border: 1px solid rgba(16,185,129,0.22); }
.mwh-s-failed     { background: rgba(239,68,68,0.1);   color: #f87171; border: 1px solid rgba(239,68,68,0.22); }
.mwh-s-invalid    { background: rgba(245,158,11,0.1);  color: #fbbf24; border: 1px solid rgba(245,158,11,0.22); }
.mwh-s-duplicate  { background: rgba(251,146,60,0.1);  color: #fb923c; border: 1px solid rgba(251,146,60,0.22); }

/* Response code */
.mwh-resp { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 800; }
.mwh-resp.ok  { color: #34d399; }
.mwh-resp.err { color: #f87171; }
.mwh-ms { font-size: 11px; color: #2a4a65; margin-left: 6px; font-weight: 600; }

/* Date */
.mwh-date { font-size: 12px; color: #5a8aaa; white-space: nowrap; font-weight: 600; }

/* Eye button */
.mwh-eye {
  opacity: 0; transform: scale(0.85);
  background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.18);
  border-radius: 10px; padding: 7px; cursor: pointer;
  display: flex; align-items: center;
  transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
}
.mwh-eye:hover { background: rgba(34,211,238,0.16); box-shadow: 0 0 14px rgba(34,211,238,0.2); }

/* Empty / Loading */
.mwh-empty { text-align: center; padding: 64px 24px; }
.mwh-empty-icon { width: 64px; height: 64px; border-radius: 20px; background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
@keyframes skelpulse { 0%,100%{opacity:0.3} 50%{opacity:0.7} }
.mwh-skel { height: 60px; background: rgba(34,211,238,0.03); animation: skelpulse 1.6s ease-in-out infinite; }

/* Pagination */
.mwh-pag-btn {
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 10px; padding: 8px 12px; color: #5a8aaa; cursor: pointer;
  display: flex; align-items: center; transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
}
.mwh-pag-btn:hover:not(:disabled) { border-color: rgba(34,211,238,0.35); color: #22d3ee; box-shadow: 0 0 14px rgba(34,211,238,0.1); transform: translateY(-1px); }
.mwh-pag-btn:active:not(:disabled) { transform: scale(0.95); }
.mwh-pag-btn:disabled { opacity: 0.3; cursor: not-allowed; }

/* ── Detail Drawer ── */
.mwh-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(6px); z-index: 50; }
.mwh-drawer {
  position: fixed; top: 0; right: 0; bottom: 0; width: 100%; max-width: 500px;
  background: linear-gradient(160deg, rgba(6,14,30,0.99), rgba(8,16,36,0.99));
  border-left: 1px solid rgba(34,211,238,0.12); z-index: 51;
  display: flex; flex-direction: column; overflow: hidden;
  backdrop-filter: blur(20px);
}
.mwh-drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 26px; border-bottom: 1px solid rgba(34,211,238,0.08); flex-shrink: 0;
  background: rgba(0,0,0,0.2);
  position: relative; overflow: hidden;
}
.mwh-drawer-head::after {
  content: ''; position: absolute; bottom: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}
.mwh-drawer-title { color: #e2f4ff; font-size: 15px; font-weight: 800; }
.mwh-close {
  background: rgba(34,211,238,0.07); border: 1px solid rgba(34,211,238,0.14);
  border-radius: 10px; padding: 8px; cursor: pointer; display: flex; align-items: center;
  transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
}
.mwh-close:hover { background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.35); transform: scale(1.05); }

.mwh-drawer-body { overflow-y: auto; padding: 24px; display: flex; flex-direction: column; gap: 18px; flex: 1; overscroll-behavior: contain; }
.mwh-drawer { overscroll-behavior: contain; }
.mwh-dl-lbl { color: #2a4a65; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 7px; }
.mwh-dl-val { color: #e2f4ff; font-size: 13.5px; font-weight: 600; }
.mwh-code { background: rgba(0,0,0,0.4); border: 1px solid rgba(34,211,238,0.1); border-radius: 12px; padding: 11px 15px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #7aaec8; word-break: break-all; }
.mwh-pre { background: rgba(0,0,0,0.45); border: 1px solid rgba(34,211,238,0.1); border-radius: 14px; padding: 16px; font-family: 'JetBrains Mono', monospace; font-size: 11.5px; color: #5a8aaa; overflow-x: auto; max-height: 200px; overflow-y: auto; white-space: pre-wrap; }
.mwh-dl-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.mwh-div-d { height: 1px; background: rgba(34,211,238,0.07); }

/* Responsive */
@media (max-width: 640px) {
  .mwh-thead th:nth-child(3), .mwh-td:nth-child(3),
  .mwh-thead th:nth-child(5), .mwh-td:nth-child(5) { display: none; }
  .mwh-drawer { max-width: 100%; }
  .mwh-h1 { font-size: 1.3rem; }
}
`;

function DetailDrawer({ log, onClose, t }) {
  if (!log) return null;
  const st = STATUS_STYLE[log.status] || STATUS_STYLE.received;
  const StIcon = st.icon;

  return (
    <>
      <motion.div className="mwh-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }} onClick={onClose}
      />
      <motion.div className="mwh-drawer"
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 340, damping: 36 }}
      >
        <div className="mwh-drawer-head">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #06b6d4, #0891b2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 16px rgba(6,182,212,0.3)",
            }}>
              <Webhook size={16} style={{ color: "#fff" }} />
            </div>
            <span className="mwh-drawer-title">{t("webhookDetails") || "Webhook Details"}</span>
          </div>
          <motion.button className="mwh-close" onClick={onClose} whileTap={{ scale: 0.93 }}>
            <X size={16} style={{ color: "#22d3ee" }} />
          </motion.button>
        </div>

        <div className="mwh-drawer-body">
          {/* Webhook ID */}
          <div>
            <div className="mwh-dl-lbl">{t("webhookId") || "Webhook ID"}</div>
            <div className="mwh-code">{log.webhook_id}</div>
          </div>

          {/* Grid row */}
          <div className="mwh-dl-grid">
            <div>
              <div className="mwh-dl-lbl">{t("direction") || "Direction"}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div className={`mwh-dir ${log.direction === "inbound" ? "in" : "out"}`} style={{ width: 28, height: 28, borderRadius: 8 }}>
                  {log.direction === "inbound" ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />}
                </div>
                <span className="mwh-dl-val" style={{ textTransform: "capitalize" }}>{log.direction}</span>
              </div>
            </div>
            <div>
              <div className="mwh-dl-lbl">{t("status") || "Status"}</div>
              <div className={`mwh-status ${st.cls}`}><StIcon size={11} />{translateStatus(log.status, t)}</div>
            </div>
            <div>
              <div className="mwh-dl-lbl">{t("event") || "Event"}</div>
              <div className="mwh-dl-val">{log.event_type}</div>
            </div>
          </div>

          {log.url && (
            <>
              <div className="mwh-div-d" />
              <div>
                <div className="mwh-dl-lbl">URL</div>
                <div className="mwh-code">{log.url}</div>
              </div>
            </>
          )}
          {log.request_payload && (
            <>
              <div className="mwh-div-d" />
              <div>
                <div className="mwh-dl-lbl">{t("payload") || "Payload"}</div>
                <pre className="mwh-pre">{typeof log.request_payload === "string"
                  ? log.request_payload
                  : JSON.stringify(log.request_payload, null, 2)}</pre>
              </div>
            </>
          )}
          {log.response_body && (
            <>
              <div className="mwh-div-d" />
              <div>
                <div className="mwh-dl-lbl">Response Body</div>
                <pre className="mwh-pre">{typeof log.response_body === "string"
                  ? log.response_body
                  : JSON.stringify(log.response_body, null, 2)}</pre>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}

export default function MerchantWebhooks() {

  const params = useParams();
  const merchantId = params?.merchantId || auth.getUserData()?.userId;

  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const { data: logsData, isLoading } = useQuery({
    queryKey: ["merchant-webhook-logs", merchantId, debouncedSearch, page, pageSize],
    queryFn: () => webhookLogService.getWebhookLogs({ merchantIds: merchantId ? [merchantId] : [], search: debouncedSearch, page, limit: pageSize }),
    enabled: Boolean(merchantId),
  });

  const logs = React.useMemo(() => {
    return (logsData?.data || []).map((log) => ({
      id: log.id,
      webhook_id: log.id,
      transaction_id: log.payment_id || "",
      event_type: log.event,
      direction: "outbound",
      status: (log.http_status >= 200 && log.http_status < 300) ? "processed" : "failed",
      response_status: log.http_status,
      url: log.url,
      request_payload: log.request_payload,
      response_body: log.response_body,
      created_date: log.created_at,
    }));
  }, [logsData]);

  const pagination = logsData || {};

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages || 1;
    const current = page;
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);
    const nums = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [page, pagination?.totalPages]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, merchantId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  return (
    <MerchantLayout currentPage="MerchantWebhooks" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{STYLE}</style>
      <div className="mwh-root">
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: "flex", alignItems: "center", gap: 16 }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 15, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              boxShadow: "0 8px 28px rgba(139,92,246,0.4)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg, rgba(255,255,255,0.12), transparent)" }} />
              <Webhook size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
            </div>
            <div>
              <h1 className="mwh-h1">{t("webhookLogs") || "Webhook Logs"}</h1>
              <p className="mwh-sub">Real-time webhook event monitoring</p>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div className="mwh-filter"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <div style={{ color: "#22d3ee" }}>
              <Filter size={14} />
            </div>
            <div className="mwh-search" style={{ flex: 1, minWidth: 200 }}>
              <Search size={14} className="mwh-search-icon" style={{ color: "#2a4a65" }} />
              <input
                placeholder={t("Search PaymentId...") || "Search by webhook ID…"}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </div>
          </motion.div>

          {/* Table */}
          <motion.div className="mwh-table-card"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.45 }}
          >
            <div style={{ overflowX: "auto" }}>
              <table className="mwh-table">
                <thead className="mwh-thead">
                  <tr>
                    <th>{t("direction") || "Direction"}</th>
                    <th>{t("webhookId") || "Webhook ID"}</th>
                    <th>{t("event") || "Event"}</th>
                    <th>{t("status") || "Status"}</th>
                    <th>{t("response") || "Response"}</th>
                    <th>{t("date") || "Date"}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="mwh-row">
                        <td className="mwh-td" colSpan={7}>
                          <div className="mwh-skel" style={{ borderRadius: 8 }} />
                        </td>
                      </tr>
                    ))
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="mwh-empty">
                          <div className="mwh-empty-icon">
                            <Webhook size={26} style={{ color: "#1e3a52" }} />
                          </div>
                          <p style={{ color: "#2a4a65", fontSize: 14, fontWeight: 700 }}>{t("noWebhookLogs") || "No webhook logs"}</p>
                          <p style={{ color: "#1e3a50", fontSize: 13, marginTop: 6, fontWeight: 500 }}>Webhook events will appear here in real-time.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logs.map((log, idx) => {
                      const st = STATUS_STYLE[log.status] || STATUS_STYLE.received;
                      const StIcon = st.icon;
                      const isOk = log.response_status >= 200 && log.response_status < 300;
                      return (
                        <motion.tr key={log.id} className="mwh-row"
                          initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.025 }}
                          onClick={() => setSelectedLog(log)}
                        >
                          <td className="mwh-td">
                            <div className={`mwh-dir ${log.direction === "inbound" ? "in" : "out"}`}>
                              {log.direction === "inbound" ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                            </div>
                          </td>
                          <td className="mwh-td">
                            <div className="mwh-wid">{log.webhook_id}</div>
                            <div className="mwh-tid">{log.transaction_id}</div>
                          </td>
                          <td className="mwh-td">
                            <span className="mwh-event">{log.event_type?.replace(/\./g, " ")}</span>
                          </td>
                          <td className="mwh-td">
                            <span className={`mwh-status ${st.cls}`}>
                              <StIcon size={11} />
                              {translateStatus(log.status, t)}
                            </span>
                          </td>
                          <td className="mwh-td">
                            <span className={`mwh-resp ${isOk ? "ok" : "err"}`}>{log.response_status || "—"}</span>
                            {log.response_time_ms && <span className="mwh-ms">{log.response_time_ms}ms</span>}
                          </td>
                          <td className="mwh-td">
                            <span className="mwh-date">
                              {format(new Date(Number(log.created_date)), "MMM d, HH:mm:ss") ?? "—"}
                            </span>
                          </td>
                          <td className="mwh-td">
                            <motion.button className="mwh-eye" whileTap={{ scale: 0.92 }}
                              onClick={(e) => { e.stopPropagation(); setSelectedLog(log); }}>
                              <Eye size={14} style={{ color: "#22d3ee" }} />
                            </motion.button>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <style>{`
                        .wh-pag { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-top: 16px; }
                        .wh-pag-info { color: #94a3b8; font-size: 13px; font-weight: 500; }
                        .wh-arr { width: 34px; height: 34px; border-radius: 10px; background: rgba(36,52,74,0.45); border: 1px solid rgba(148,163,184,0.25); display: flex; align-items: center; justify-content: center; color: #cbd5e1; cursor: pointer; transition: all 0.2s ease; }
                        .wh-arr:hover:not(:disabled) { background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.32); color: #22d3ee; transform: scale(1.05); }
                        .wh-arr:disabled { opacity: 0.35; cursor: not-allowed; }
                        .wh-pg { min-width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid transparent; color: #cbd5e1; transition: all 0.2s ease; }
                        .wh-pg:hover { background: rgba(34,211,238,0.1); color: #22d3ee; }
                        .wh-pg.on { background: linear-gradient(135deg, rgba(14,165,233,0.2), rgba(34,211,238,0.1)); border-color: rgba(34,211,238,0.3); color: #22d3ee; }
                      `}</style>

              {pagination.totalPages > 1 && (
                <motion.div className="wh-pag" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                  <div className="wh-pag-info">
                    Showing {pagination.total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, pagination.total || 0)} of {pagination.total || 0}
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 text-sm outline-none"
                    >
                      {[10, 20, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size} / page
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      disabled={page === 1}
                      className="wh-arr"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {pageNumbers.map((num) => (
                      <button
                        key={num}
                        className={`wh-pg${num === page ? " on" : ""}`}
                        onClick={() => setPage(num)}
                      >
                        {num}
                      </button>
                    ))}

                    <button
                      onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages || 1))}
                      disabled={page >= (pagination.totalPages || 1)}
                      className="wh-arr"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {pagination.totalPages <= 1 && (
                <div className="wh-pag px-4 mb-2" style={{ justifyContent: "space-between" }}>
                  <div className="wh-pag-info">
                    Showing {pagination.total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, pagination.total || 0)} of {pagination.total || 0}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 text-sm outline-none"
                    >
                      {[10, 20, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {selectedLog && <DetailDrawer log={selectedLog} onClose={() => setSelectedLog(null)} t={t} />}
      </AnimatePresence>
    </MerchantLayout>
  );
}